import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  saveChapterVersePosition,
  getChapterVersePosition,
  getBookmarks,
  removeBookmark,
  toggleBookmark,
  buildBookmarkId,
  EVENT_BOOKMARKS_CHANGED,
} from "../../bible/readingPositionStorage";

export const useReadingPosition = ({
  chapterId,
  bookName,
  chapterNumber = 1,
  currentVerse = 1,
  isScrolling = false,
  focusVerse,
  verseTextMap = {},
  scrollToVerse,
  uid,
  persist = true,
}) => {
  const [resumeVerse, setResumeVerse] = useState(null);
  const [bookmarks, setBookmarks] = useState(() => getBookmarks(uid));
  const lastSavedRef = useRef({ chapterId: "", verse: 0, ts: 0 });
  const hasScrolledInChapterRef = useRef(false);

  const syncBookmarks = useCallback(() => {
    setBookmarks(getBookmarks(uid));
  }, [uid]);

  useEffect(() => {
    syncBookmarks();
    window.addEventListener(EVENT_BOOKMARKS_CHANGED, syncBookmarks);
    window.addEventListener("storage", syncBookmarks);
    return () => {
      window.removeEventListener(EVENT_BOOKMARKS_CHANGED, syncBookmarks);
      window.removeEventListener("storage", syncBookmarks);
    };
  }, [syncBookmarks]);

  useEffect(() => {
    hasScrolledInChapterRef.current = false;
    if (!chapterId || focusVerse) {
      setResumeVerse(null);
      return;
    }
    const saved = getChapterVersePosition(chapterId, uid);
    if (saved && saved > 1) {
      setResumeVerse(saved);
    } else {
      setResumeVerse(null);
    }
  }, [chapterId, focusVerse, uid]);

  useEffect(() => {
    if (!chapterId || !persist) return;
    if (isScrolling) {
      hasScrolledInChapterRef.current = true;
    }
    if (!hasScrolledInChapterRef.current && currentVerse <= 1) return;

    const now = Date.now();
    if (
      lastSavedRef.current.chapterId === chapterId &&
      lastSavedRef.current.verse === currentVerse &&
      now - lastSavedRef.current.ts < 500
    ) {
      return;
    }
    lastSavedRef.current = { chapterId, verse: currentVerse, ts: now };
    saveChapterVersePosition(chapterId, currentVerse, uid);
  }, [chapterId, currentVerse, isScrolling, uid, persist]);

  const handleJumpToResumeVerse = useCallback(() => {
    if (resumeVerse && scrollToVerse) {
      scrollToVerse(resumeVerse);
    }
    setResumeVerse(null);
  }, [resumeVerse, scrollToVerse]);

  const dismissResumeVerse = useCallback(() => {
    setResumeVerse(null);
  }, []);

  const chapterBookmarks = useMemo(
    () => bookmarks.filter((b) => b.chapterId === chapterId),
    [bookmarks, chapterId]
  );

  const isCurrentVerseBookmarked = useMemo(() => {
    if (!chapterId || !currentVerse) return false;
    const targetId = buildBookmarkId(chapterId, currentVerse);
    return bookmarks.some((b) => b.id === targetId);
  }, [bookmarks, chapterId, currentVerse]);

  const handleToggleCurrentBookmark = useCallback(() => {
    if (!chapterId) return;
    const verse = Number(currentVerse) || 1;
    const verseText = verseTextMap[String(verse)] || "";
    const res = toggleBookmark(
      {
        chapterId,
        bookName,
        chapterNumber,
        verse,
        verseText,
      },
      uid
    );
    setBookmarks(res.list);
  }, [bookName, chapterId, chapterNumber, currentVerse, uid, verseTextMap]);

  const handleRemoveBookmark = useCallback(
    (id) => {
      const next = removeBookmark(id, uid);
      setBookmarks(next);
    },
    [uid]
  );

  return {
    resumeVerse,
    handleJumpToResumeVerse,
    dismissResumeVerse,
    bookmarks,
    chapterBookmarks,
    isCurrentVerseBookmarked,
    handleToggleCurrentBookmark,
    handleRemoveBookmark,
  };
};

export default useReadingPosition;
